// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint @typescript-eslint/no-shadow: off */
import {axisBottom, axisLeft, axisRight} from 'd3-axis'
import {bisector, max as d3max} from 'd3-array'
import {pointer, select} from 'd3-selection'
import {scaleLinear, scaleTime} from 'd3-scale'
import {timeDay, timeMonth} from 'd3-time'
import d3Tip from '../d3/tip'
import {line as d3line} from 'd3-shape'
import {format} from 'd3-format'
import formatSymbol from '../d3/format-symbol'
import {timeFormat} from 'd3-time-format'

function numberFormat(value: number): string {
  const fn = format(',')
  return fn(value)
}

// Dimensions
const margin = {
  top: 20,
  right: 40,
  bottom: 30,
  left: 40,
}

function totalChangeUpdate(total: number, element: Element) {
  if (total < 0) {
    element.classList.add('is-decrease')
  } else if (total > 0) {
    element.classList.add('is-increase')
  }
  element.querySelector<HTMLElement>('.js-change-num')!.textContent = numberFormat(Math.abs(total))
}

function percentChangeUpdate(total: number, element: Element) {
  if (total < 0) {
    element.classList.add('is-decrease')
    element.querySelector<HTMLElement>('.js-change-num')!.textContent = `${numberFormat(Math.abs(total))}% decrease`
  } else if (total > 0) {
    element.classList.add('is-increase')
    element.querySelector<HTMLElement>('.js-change-num')!.textContent = `${numberFormat(Math.abs(total))}% increase`
  } else if (total === 0) {
    element.querySelector<HTMLElement>('.js-change-num')!.textContent = `${numberFormat(Math.abs(total))}% increase`
  }
}

interface GraphData {
  error: string
  timespan: {
    period: string
    beginning: number
    ending: number
  }
  metrics: Array<{
    name: string
    display_name: string
    max: number
    total: number
    total_change: number
    percent_change: number
    counts: Array<{count: number; date: number}>
  }>
}

export default function render(container: HTMLElement, data: GraphData) {
  if (!data || data.error != null) {
    return
  }

  const card = container.closest<HTMLElement>('.js-dashboards-overview-card')!
  const period = data.timespan.period

  const longFormat = timeFormat('%A, %B %-d, %Y')
  const bisectAtDate = bisector(d => d.date).left

  const width = container.offsetWidth - margin.right - margin.left
  const height = 150 - margin.top - margin.bottom

  for (const [i, metric] of data.metrics.entries()) {
    const totalElement = card.querySelector(`.js-metric[data-metric-index="${i}"] .js-total`)

    if (totalElement != null) {
      totalElement.textContent = numberFormat(metric.total)

      totalChangeUpdate(
        metric.total_change,
        card.querySelector<HTMLElement>(`.js-metric[data-metric-index="${i}"] .js-total-change`)!,
      )
      percentChangeUpdate(
        metric.percent_change,
        card.querySelector<HTMLElement>(`.js-metric[data-metric-index="${i}"] .js-percentage-change`)!,
      )
    }
  }

  if (!data.metrics.some(m => m.total)) {
    card.classList.add('is-no-activity')
    return
  }

  const tip = d3Tip()
    .attr('class', 'svg-tip total-unique comparison')
    .offset([-10, 0])
    .html(function (d) {
      let toolTipColumns = ''
      let dateText
      if (period === 'year') {
        // eslint-disable-next-line i18n-text/no-en
        dateText = `Week of ${longFormat(d.date)}`
      } else {
        dateText = longFormat(d.date)
      }
      const tipColumnWidth = 270 / data.metrics.length

      for (const [i, metric] of data.metrics.entries()) {
        // eslint-disable-next-line github/unescaped-html-literal
        toolTipColumns += `<li class='totals metric-${i}' style='width:${tipColumnWidth}px'> <strong>${numberFormat(
          d[metric.name],
        )}</strong> ${metric.display_name} </li>`
      }
      // eslint-disable-next-line github/unescaped-html-literal
      return `<span class='title'>${dateText}</span> <ul> ${toolTipColumns} </ul>`
    })

  const x = scaleTime().range([0, width])
  const y = scaleLinear().range([height, 0])
  let stepNumber = 1

  let tickCount

  if (period === 'year') {
    tickCount = timeMonth
  } else if (period === 'week') {
    stepNumber = 1
    tickCount = timeDay
  } else {
    stepNumber = 2
    tickCount = timeDay
  }

  const xAxis = axisBottom(x)
    .tickSize(height + 5)
    .tickPadding(10)
    .ticks(tickCount.every(stepNumber))
    .tickFormat(d => {
      if (period === 'year') {
        return timeFormat('%B')(d)
      } else if (period === 'week' && d.getHours() >= 8) {
        // avoid displaying ticks for 8 AM and 4 PM
        return ''
      } else {
        return timeFormat('%a %-d')(d)
      }
    })
  let yAxis = axisLeft(y).ticks(3).tickFormat(formatSymbol)

  const line = d3line()
    .x(d => x(d.date))
    .y(d => y(d.count))

  const vis = select(container)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .attr('class', 'vis')
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)
    .call(tip)
  x.domain([data.timespan.beginning, data.timespan.ending])
  y.domain([0, d3max(data.metrics.map(m => m.max))])
  vis.append('g').attr('class', 'x axis').call(xAxis).selectAll('text').attr('text-anchor', 'middle')
  vis.append('g').attr('class', 'y axis').call(yAxis)

  const allCounts = data.metrics.map(m => m.counts)

  // draw lines for each metric
  vis
    .selectAll('path.path')
    .data(allCounts)
    .enter()
    .append('path')
    .attr('class', (d, i) => `dots total metric-${i || 0}`)
    .attr('d', line)

  // create groups to contain the dots for each metric
  const dots = vis
    .selectAll('g.dots')
    .data(allCounts)
    .enter()
    .append('g')
    .attr('class', (d, i) => `dots totals metric-${i || 0}`)

  // draw the dots for each metric
  dots.each(function () {
    /* eslint-disable-next-line @typescript-eslint/no-invalid-this */
    select(this)
      .selectAll('circle')
      .data(d => d)
      .enter()
      .append('circle')
      .attr('cx', d => x(d.date))
      .attr('cy', d => y(d.count))
      .attr('r', 4)
  })
  yAxis = axisRight(y).ticks(3).tickFormat(formatSymbol)
  vis.append('g').attr('class', 'y axis unique').attr('transform', `translate(${width}, 0)`).call(yAxis)
  vis
    .append('rect')
    .attr('class', 'overlay')
    .attr('width', width)
    .attr('height', height)
    .on('mousemove', function showFocusedValue(event) {
      const tipData = {}
      const x0 = x.invert(pointer(event)[0])
      const firstMetric = data.metrics[0].counts
      const idx = bisectAtDate(firstMetric, x0, 1)
      const d0 = firstMetric[idx - 1]
      const d1 = firstMetric[idx]
      const adjustedIndex = d1 && x0 - d0.date > d1.date - x0 ? idx : idx - 1
      tipData.date = firstMetric[adjustedIndex].date

      for (const metric of data.metrics) {
        tipData[metric.name] = metric.counts[adjustedIndex].count
      }

      const elements = vis.selectAll('g.dots circle').filter(data => data.date === tipData.date)
      /* eslint-disable-next-line @typescript-eslint/no-invalid-this */
      tip.show.call(this, {target: elements.node()}, tipData)
    })
    .on('mouseout', function () {
      setTimeout(tip.hide, 500)
    })
}
